import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { camerasAPI, devicesAPI, getImageUrl } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useLiveStreamStore } from '../stores/liveStreamStore';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { formatDateTime } from '../utils/formatDateTime';
import {
  VideoCameraIcon,
  SignalIcon,
  SignalSlashIcon,
  EyeIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  XMarkIcon,
  PlusIcon,
  PlayIcon,
  StopIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  MagnifyingGlassIcon,
  WifiIcon,
  PencilSquareIcon,
  TrashIcon,
  CpuChipIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal';
import DetectionPanel from '../components/cameras/DetectionPanel';
import DetectionEventFeed from '../components/cameras/DetectionEventFeed';
import DiscoverCamerasModal from '../components/cameras/DiscoverCamerasModal';
import CameraConfigPage from './CameraConfigPage';

export default function CamerasPage() {
  const { selectedSite } = useSiteStore();
  const [viewMode, setViewMode] = useState('grid');
  const [gridLayout, setGridLayout] = useState('auto'); // 'auto', '2x2', '3x3'
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [showDeviceConfigModal, setShowDeviceConfigModal] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [showInactiveCameras, setShowInactiveCameras] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeCameraTab, setActiveCameraTab] = useState('live');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['cameras', selectedSite?.id, showInactiveCameras],
    queryFn: () => camerasAPI.getCameras(selectedSite?.id, { includeInactive: showInactiveCameras ? 'true' : 'false' }),
    // Poll less aggressively — the cameras list is heavy (includes detectionConfig
    // per camera). Live status comes via WebSocket; a slower refetch is enough
    // to catch new/removed cameras without rerendering the embedded config panel.
    refetchInterval: 5 * 60_000,
    staleTime: 30_000,
  });

  const allCameras = data?.data?.data || [];

  // Filter cameras: show only active by default, or all if toggle is checked
  const cameras = showInactiveCameras ? allCameras : allCameras.filter(c => c.isActive);

  // Mutations for soft delete and delete
  const softDeleteMutation = useMutation({
    mutationFn: (cameraId) => camerasAPI.updateCamera(cameraId, { isActive: false }),
    onSuccess: () => {
      toast.success('Camera deactivated successfully');
      // Clear all camera-related queries from cache
      queryClient.removeQueries({ queryKey: ['cameras'] });
      // Explicitly refetch with current parameters
      refetch();
      setSelectedCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to deactivate camera');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (cameraId) => camerasAPI.updateCamera(cameraId, { isActive: true }),
    onSuccess: () => {
      toast.success('Camera restored successfully');
      queryClient.removeQueries({ queryKey: ['cameras'] });
      refetch();
      setSelectedCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to restore camera');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (cameraId) => camerasAPI.deleteCamera(cameraId),
    onSuccess: () => {
      toast.success('Camera deleted permanently');
      queryClient.removeQueries({ queryKey: ['cameras'] });
      refetch();
      setSelectedCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete camera');
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }) => camerasAPI.updateCamera(id, { name }),
    onSuccess: () => {
      toast.success('Camera renamed');
      queryClient.removeQueries({ queryKey: ['cameras'] });
      refetch();
      setEditingNameId(null);
    },
    onError: () => toast.error('Failed to rename camera'),
  });

  const handleRenameSubmit = (camera) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed || trimmed === camera.name) { setEditingNameId(null); return; }
    renameMutation.mutate({ id: camera.id, name: trimmed });
  };

  // Status counts should only include active cameras
  const activeCameras = allCameras.filter(c => c.isActive);
  const statusCounts = {
    online: activeCameras.filter(c => c.status === 'online').length,
    offline: activeCameras.filter(c => c.status === 'offline').length,
    error: activeCameras.filter(c => c.status === 'error').length,
  };

  const handleCameraAdded = () => {
    setShowAddModal(false);
    queryClient.invalidateQueries({ queryKey: ['cameras'] });
    refetch();
  };

  const handleSoftDelete = (camera) => {
    setDeleteTarget({
      camera,
      type: 'soft',
      title: 'Deactivate Camera',
      message: `Are you sure you want to deactivate "${camera.name}"? It can be restored later.`,
      confirmLabel: 'Deactivate',
      variant: 'warning',
    });
  };

  const handleHardDelete = (camera) => {
    setDeleteTarget({
      camera,
      type: 'hard',
      title: 'Delete Camera Permanently',
      message: `Are you sure you want to permanently delete "${camera.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete Permanently',
      variant: 'danger',
    });
  };

  const handleRestore = (camera) => {
    setDeleteTarget({
      camera,
      type: 'restore',
      title: 'Restore Camera',
      message: `Restore "${camera.name}" to active status?`,
      confirmLabel: 'Restore',
      variant: 'info',
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    switch (deleteTarget.type) {
      case 'soft':
        softDeleteMutation.mutate(deleteTarget.camera.id);
        break;
      case 'hard':
        deleteMutation.mutate(deleteTarget.camera.id);
        break;
      case 'restore':
        restoreMutation.mutate(deleteTarget.camera.id);
        break;
      default:
        break;
    }
    setDeleteTarget(null);
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Camera Management</h1>
          <p className="page-description">Monitor and configure safety cameras</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="icon-btn"
            title="Refresh"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowDeviceConfigModal(true)}
            className="btn btn-secondary flex items-center gap-2"
            title="Configure AI device hardware settings"
          >
            <CpuChipIcon className="w-5 h-5" />
            <span className="hidden sm:inline">AI Device</span>
          </button>
          <button
            onClick={() => setShowDiscoverModal(true)}
            className="btn btn-secondary flex items-center gap-2"
            title="Discover ONVIF cameras on network"
          >
            <WifiIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Discover</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Add Camera</span>
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatusCard
          title="Online"
          count={statusCounts.online}
          total={cameras.length}
          color="green"
          icon={SignalIcon}
          index={0}
        />
        <StatusCard
          title="Offline"
          count={statusCounts.offline}
          total={cameras.length}
          color="gray"
          icon={SignalSlashIcon}
          index={1}
        />
        <StatusCard
          title="Error"
          count={statusCounts.error}
          total={cameras.length}
          color="red"
          icon={ExclamationTriangleIcon}
          index={2}
        />
      </div>

      {/* Show Inactive Cameras Toggle */}
      {!isLoading && allCameras.some(c => !c.isActive) && (
        <div className="glass-card p-4 flex items-center justify-between">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={showInactiveCameras}
              onChange={(e) => setShowInactiveCameras(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600"
            />
            <span className="text-sm text-slate-600 group-hover:text-slate-800">
              Show inactive cameras ({allCameras.filter(c => !c.isActive).length})
            </span>
          </label>
        </div>
      )}

      {/* Three-column Setup Layout: cameras rail | preview+config */}
      {isLoading ? (
        <div className="glass-card p-12 text-center text-slate-500">Loading cameras...</div>
      ) : cameras.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <VideoCameraIcon className="w-10 h-10" />
          </div>
          <h3 className="empty-state-title">No cameras configured</h3>
          <p className="empty-state-description">Add cameras to start monitoring safety across your site.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add First Camera
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Cameras List Rail */}
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <div className="glass-card overflow-hidden">
              <div className="p-3 border-b flex items-center gap-2 bg-slate-50">
                <CameraIcon className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Cameras ({cameras.length})</span>
              </div>
              <div className="max-h-[calc(100vh-280px)] overflow-y-auto divide-y">
                {cameras.map((camera) => {
                  const isSelected = selectedCamera?.id === camera.id;
                  const effectiveStatus = (camera.aiDevice && camera.aiDevice.status !== 'online')
                    ? 'offline'
                    : (camera.status || 'offline');
                  return (
                    <button
                      key={camera.id}
                      onClick={() => {
                        setSelectedCamera(camera);
                        setActiveCameraTab('live');
                      }}
                      className={clsx(
                        'w-full text-left p-3 hover:bg-blue-50 transition-colors flex items-start gap-2',
                        isSelected && 'bg-blue-50 border-l-2 border-blue-500'
                      )}
                    >
                      <span
                        className={clsx(
                          'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                          effectiveStatus === 'online' ? 'bg-green-500' :
                          effectiveStatus === 'error' ? 'bg-red-500' : 'bg-slate-400'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        {/* Camera name with inline edit */}
                        <div className="flex items-center gap-1 group/name">
                          {editingNameId === camera.id ? (
                            <input
                              autoFocus
                              className="text-sm font-medium text-slate-800 border-b border-blue-500 outline-none bg-transparent w-full"
                              value={editingNameValue}
                              onChange={e => setEditingNameValue(e.target.value)}
                              onBlur={() => handleRenameSubmit(camera)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleRenameSubmit(camera);
                                if (e.key === 'Escape') setEditingNameId(null);
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              <p className="text-sm font-medium text-slate-800 truncate flex-1">{camera.name}</p>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setEditingNameId(camera.id);
                                  setEditingNameValue(camera.name);
                                }}
                                className="opacity-0 group-hover/name:opacity-100 transition-opacity text-slate-400 hover:text-blue-500 flex-shrink-0"
                                title="Rename camera"
                              >
                                <PencilSquareIcon className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                        {/* RTSP URL */}
                        <p className="text-[10px] text-slate-400 truncate font-mono mt-0.5" title={camera.rtspUrl}>
                          {camera.rtspUrl || 'No RTSP URL'}
                        </p>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                          <span>{camera.aiDevice?.status === 'online' ? 'AI online' : camera.aiDevice ? 'AI offline' : 'No AI device'}</span>
                          <span>·</span>
                          <span>{camera.lastSeen ? formatDateTime(camera.lastSeen) : 'No frames yet'}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Preview + Config Panels (embedded CameraConfigPage) */}
          <div className="col-span-12 md:col-span-9 lg:col-span-10">
            {selectedCamera ? (
              <div className="space-y-4">
                <div className="glass-card p-3">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{selectedCamera.name}</h2>
                      <p className="text-sm text-slate-500">
                        {selectedCamera.location || 'No location'} - {selectedCamera.aiDevice?.name || 'No AI device assigned'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'live', label: 'Live', icon: PlayIcon },
                        { id: 'events', label: 'Detection Events', icon: CpuChipIcon },
                        { id: 'config', label: 'Configuration', icon: Cog6ToothIcon },
                        { id: 'health', label: 'Health', icon: SignalIcon },
                      ].map((tab) => {
                        const TabIcon = tab.icon;
                        const active = activeCameraTab === tab.id;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveCameraTab(tab.id)}
                            className={clsx(
                              'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                              active
                                ? 'border-slate-900 bg-slate-900 text-white'
                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                            )}
                          >
                            <TabIcon className="w-4 h-4" />
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {activeCameraTab === 'live' && <InlineCameraLivePanel camera={selectedCamera} />}
                {activeCameraTab === 'events' && <InlineDetectionEventsPanel camera={selectedCamera} />}
                {activeCameraTab === 'config' && (
                  <CameraConfigPage
                    cameraIdOverride={selectedCamera.id}
                    initialCamera={selectedCamera}
                    embedded
                  />
                )}
                {activeCameraTab === 'health' && <CameraHealthPanel camera={selectedCamera} />}
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <CameraIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700">Select a camera</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Choose a camera from the list to view its live preview and configure detection settings.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Camera Modal */}
      {editingCamera && (
        <EditCameraModal
          camera={editingCamera}
          onClose={() => setEditingCamera(null)}
          onSuccess={() => {
            setEditingCamera(null);
            queryClient.invalidateQueries({ queryKey: ['cameras'] });
            refetch();
          }}
        />
      )}

      {/* Add Camera Modal */}
      {showAddModal && (
        <AddCameraModal
          siteId={selectedSite?.id}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleCameraAdded}
        />
      )}

      {/* AI Device Config Modal */}
      {showDeviceConfigModal && (
        <DeviceConfigModal
          cameras={cameras}
          onClose={() => setShowDeviceConfigModal(false)}
        />
      )}

      {/* ONVIF Discovery Modal */}
      <DiscoverCamerasModal
        isOpen={showDiscoverModal}
        onClose={() => setShowDiscoverModal(false)}
        siteId={selectedSite?.id}
        onCameraAdded={(cameraData) => {
          setShowDiscoverModal(false);
          setShowAddModal(true);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.title || 'Delete Camera'}
        message={deleteTarget?.message || ''}
        confirmLabel={deleteTarget?.confirmLabel || 'Delete'}
        variant={deleteTarget?.variant || 'danger'}
      />
    </div>
  );
}

function getEffectiveCameraStatus(camera) {
  return (camera.aiDevice && camera.aiDevice.status !== 'online')
    ? 'offline'
    : (camera.status || 'offline');
}

function InlineCameraLivePanel({ camera }) {
  const {
    connected,
    connect,
    subscribeCamera,
    unsubscribeCamera,
    requestSnapshot,
    activeStreams,
    setAnnotated,
  } = useLiveStreamStore();
  const [streamIntent, setStreamIntent] = useState(false);
  const [quality, setQuality] = useState('medium');
  const [detectionMode, setDetectionMode] = useState(false);
  const streamState = activeStreams[camera.id];
  const effectiveStatus = getEffectiveCameraStatus(camera);
  // Show the LIVE / Stop affordance based on the user's intent to stream, but let
  // a real stream error (device offline, subscribe failure) revert the UI to the
  // poster + Start button instead of stranding on Stop with a dead/frozen frame.
  const isStreaming = streamIntent && streamState?.status !== 'error';

  // Fetch the full camera detail so we can use its saved reference image
  // (detectionConfig.referenceImage — a clean snapshot of the scene captured
  // during camera setup) as the poster, instead of relying only on the last
  // alert thumbnail (which always has a person + bounding boxes drawn over it
  // and is a poor representation of "what this camera sees").
  //
  // This deduplicates with CameraConfigPage's identical query when the user
  // visits the Configuration tab — React Query serves both from one cache
  // entry.
  const { data: cameraDetailData } = useQuery({
    queryKey: ['camera', camera.id],
    queryFn: () => camerasAPI.getCamera(camera.id),
    enabled: !!camera.id,
    staleTime: 60_000,
  });
  const referenceImage = cameraDetailData?.data?.data?.detectionConfig?.referenceImage
    || (typeof cameraDetailData?.data?.data?.detectionConfig === 'string'
        ? (() => { try { return JSON.parse(cameraDetailData.data.data.detectionConfig).referenceImage; } catch { return null; } })()
        : null);

  useEffect(() => {
    if (!connected) connect();
    return () => {
      unsubscribeCamera(camera.id, camera.aiDevice?.id);
    };
  }, [camera.id]);

  const startStream = () => {
    subscribeCamera(camera.id, {
      quality,
      fps: quality === 'low' ? 5 : quality === 'high' ? 15 : 10,
      deviceId: camera.aiDevice?.id,
      annotated: detectionMode,
    });
    setStreamIntent(true);
  };

  const stopStream = () => {
    unsubscribeCamera(camera.id, camera.aiDevice?.id);
    setStreamIntent(false);
  };

  const toggleDetection = () => {
    const next = !detectionMode;
    setDetectionMode(next);
    if (isStreaming) setAnnotated(camera.id, next);
  };

  return (
    <div className="glass-card overflow-hidden">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="p-4">
          <div className="relative aspect-video rounded-xl bg-slate-950 overflow-hidden border border-slate-200">
            {(() => {
              // Pick the best image to show in this order:
              //   1. live frame (when streaming)
              //   2. cached websocket snapshot (last frame received)
              //   3. lastSnapshotUrl — image from the most recent alert on this camera
              //   4. referenceImage saved on this camera's detectionConfig
              // Falls back to the empty-state placeholder if nothing is available.
              const liveImg = streamState?.frame || streamState?.snapshot;
              if (liveImg) {
                return <img src={liveImg} alt={`${camera.name} live feed`} className="w-full h-full object-contain" />;
              }
              // Poster priority:
              //   1. referenceImage — the clean scene snapshot saved during
              //      camera config (base64 data: URL). What the user really
              //      wants here.
              //   2. lastSnapshotUrl — the most recent alert image (presigned
              //      MinIO URL). Has bounding boxes + a person in it, so only
              //      use when the cleaner reference doesn't exist.
              //   3. nothing — falls through to placeholder.
              const rawPoster = referenceImage || camera.lastSnapshotUrl;
              const posterImg = getImageUrl(rawPoster);
              if (posterImg) {
                return (
                  <>
                    {/* key={posterImg} forces React to mount a fresh <img> when
                        the source changes (camera switch). Without it React
                        reuses the same DOM node and any inline style applied
                        by a stale onError handler from the previous camera
                        sticks around, hiding the new image. */}
                    <img
                      key={posterImg}
                      src={posterImg}
                      alt={`${camera.name} last snapshot`}
                      className="w-full h-full object-contain opacity-80"
                    />
                    {/* Soft veil so the 'Start Live' affordance reads as an action over a still */}
                    <div className="absolute inset-0 bg-slate-950/30 pointer-events-none" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white pointer-events-none">
                      <VideoCameraIcon className="w-12 h-12 mb-2 drop-shadow-lg" />
                      <p className="text-sm font-semibold drop-shadow-lg">
                        {effectiveStatus === 'online' ? 'Click Start Live to stream' : 'Camera device is offline'}
                      </p>
                      <p className="mt-1 text-xs text-slate-200 drop-shadow">Showing last available snapshot</p>
                    </div>
                  </>
                );
              }
              return (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                  <VideoCameraIcon className="w-14 h-14 mb-3" />
                  <p className="text-sm font-semibold text-slate-300">
                    {effectiveStatus === 'online' ? 'Start the live feed' : 'Camera device is offline'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {camera.aiDevice ? camera.aiDevice.name : 'Assign an AI device before streaming'}
                  </p>
                </div>
              );
            })()}

            {streamState?.status === 'streaming' && (
              <div className="absolute left-3 top-3 flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white">
                <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                LIVE
              </div>
            )}
            {!isStreaming && (referenceImage || camera.lastSnapshotUrl) && (
              <div className="absolute left-3 top-3 flex items-center gap-2 rounded-lg bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-slate-100 backdrop-blur-sm">
                <PhotoIcon className="w-3.5 h-3.5" />
                Snapshot
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {!isStreaming ? (
                <button
                  onClick={startStream}
                  disabled={effectiveStatus !== 'online'}
                  className="btn btn-primary"
                >
                  <PlayIcon className="w-4 h-4" />
                  Start Live
                </button>
              ) : (
                <button onClick={stopStream} className="btn btn-secondary">
                  <StopIcon className="w-4 h-4" />
                  Stop
                </button>
              )}
              <button
                onClick={() => {
                  requestSnapshot(camera.id, true, camera.aiDevice?.id);
                  toast.success('Snapshot requested');
                }}
                disabled={effectiveStatus !== 'online'}
                className="btn btn-secondary"
              >
                <CameraIcon className="w-4 h-4" />
                Snapshot
              </button>
              <button
                onClick={toggleDetection}
                disabled={!isStreaming}
                className={clsx('btn', detectionMode ? 'btn-primary' : 'btn-secondary')}
              >
                <CpuChipIcon className="w-4 h-4" />
                AI Overlay
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-500">
              Quality
              <select
                value={quality}
                onChange={(e) => setQuality(e.target.value)}
                disabled={isStreaming}
                className="input py-2 px-3 text-sm min-w-[130px]"
              >
                <option value="low">Low (5 FPS)</option>
                <option value="medium">Medium (10 FPS)</option>
                <option value="high">High (15 FPS)</option>
              </select>
            </label>
          </div>
        </div>

        <div className="border-t xl:border-l xl:border-t-0 border-slate-200">
          <DetectionPanel detections={streamState?.detections} />
        </div>
      </div>
    </div>
  );
}

function InlineDetectionEventsPanel({ camera }) {
  const { activeStreams } = useLiveStreamStore();
  const streamState = activeStreams[camera.id];

  return (
    <div className="glass-card overflow-hidden">
      <div className="border-b border-slate-200 p-4">
        <h3 className="font-semibold text-slate-900">Detection Events</h3>
        <p className="text-sm text-slate-500">Events appear here while the live stream is running with AI overlay enabled.</p>
      </div>
      <div className="h-[480px]">
        <DetectionEventFeed detections={streamState?.detections} />
      </div>
    </div>
  );
}

function CameraHealthPanel({ camera }) {
  const effectiveStatus = getEffectiveCameraStatus(camera);
  const rows = [
    ['Camera status', effectiveStatus],
    ['AI device', camera.aiDevice?.name || 'Not assigned'],
    ['AI device status', camera.aiDevice?.status || 'N/A'],
    ['Last seen', camera.lastSeen ? formatDateTime(camera.lastSeen) : 'Never'],
    ['RTSP URL', camera.rtspUrl || 'Not configured'],
    ['Location', camera.location || 'No location'],
  ];

  return (
    <div className="glass-card p-5">
      <h3 className="font-semibold text-slate-900">Camera Health</h3>
      <div className="mt-4 divide-y divide-slate-100">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-4 py-3 text-sm">
            <span className="text-slate-500">{label}</span>
            <span className="max-w-[70%] truncate text-right font-medium text-slate-800">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusCard({ title, count, total, color, icon: Icon, index }) {
  const colorConfig = {
    green: {
      gradient: 'from-green-500 to-emerald-500',
      glow: '',
      text: 'text-green-400',
      bg: 'bg-green-500/20',
      border: 'border-green-500/30',
    },
    gray: {
      gradient: 'from-gray-400 to-slate-500',
      glow: '',
      text: 'text-slate-500',
      bg: 'bg-slate-100',
      border: 'border-slate-200',
    },
    red: {
      gradient: 'from-red-500 to-rose-500',
      glow: '',
      text: 'text-red-400',
      bg: 'bg-red-500/20',
      border: 'border-red-500/30',
    },
  };

  const config = colorConfig[color];

  return (
    <div
      className={clsx(
        'glass-card p-5 transition-all duration-300 hover:scale-[1.02] animate-slide-up',
        count > 0 && color === 'red' && 'border-red-500/50'
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-center gap-4">
        <div className={clsx(
          'w-14 h-14 rounded-xl flex items-center justify-center',
          config.bg,
          config.border,
          'border',
          count > 0 && config.glow
        )}>
          <Icon className={clsx('w-7 h-7', config.text)} />
        </div>
        <div>
          <p className="text-3xl font-bold text-slate-800">{count}</p>
          <p className="text-sm text-slate-500">{title}</p>
        </div>
        {total > 0 && (
          <div className="ml-auto">
            <span className="text-sm text-slate-400">
              {Math.round((count / total) * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CameraCard({ camera, index, onView, onConfigure, onEdit, onSoftDelete, onHardDelete, onRestore }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { connect, connected, subscribeCamera, unsubscribeCamera, activeStreams, requestSnapshot } = useLiveStreamStore();

  const statusConfig = {
    online: {
      dot: 'bg-green-500',
      glow: 'shadow-[0_0_10px_rgba(34,197,94,0.5)]',
      badge: 'badge-success',
      pulse: true,
    },
    offline: {
      dot: 'bg-slate-400',
      glow: '',
      badge: 'bg-slate-100 text-slate-500 border border-slate-200',
      pulse: false,
    },
    error: {
      dot: 'bg-red-500',
      glow: 'shadow-[0_0_10px_rgba(239,68,68,0.5)]',
      badge: 'badge-critical',
      pulse: true,
    },
  };

  // If AI device is assigned but offline, camera is effectively offline regardless of camera.status
  const effectiveStatus = (camera.aiDevice && camera.aiDevice.status !== 'online')
    ? 'offline'
    : (camera.status || 'offline');
  const config = statusConfig[effectiveStatus] || statusConfig.offline;

  // Use stored reference image or live snapshot
  const streamState = activeStreams[camera.id];
  const liveSnapshot = streamState?.snapshot || streamState?.frame;
  const referenceImage = liveSnapshot || camera.detectionConfig?.referenceImage;
  const hasImage = !!referenceImage;

  // Handle refresh thumbnail
  const handleRefresh = async (e) => {
    e.stopPropagation();
    if (camera.aiDevice?.status !== 'online') {
      toast.error('AI device is offline');
      return;
    }

    setIsRefreshing(true);

    // Connect if not connected
    if (!connected) {
      connect();
    }

    // Subscribe and request snapshot
    subscribeCamera(camera.id, { quality: 'medium', fps: 1 });

    // Wait a moment then request snapshot
    setTimeout(() => {
      requestSnapshot(camera.id, false);
    }, 500);

    // Unsubscribe after getting snapshot
    setTimeout(() => {
      unsubscribeCamera(camera.id);
      setIsRefreshing(false);
    }, 3000);
  };

  return (
    <div
      className={clsx(
        "glass-card overflow-hidden group animate-scale-in",
        !camera.isActive && 'opacity-60 border-slate-300'
      )}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Camera thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
        {/* Grid pattern overlay (shown when no image) */}
        {!hasImage && <div className="absolute inset-0 bg-grid-pattern opacity-30" />}

        {/* Show stored reference image or live snapshot */}
        {isRefreshing ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-blue-400 border-t-white rounded-full animate-spin" />
            <span className="absolute mt-16 text-xs text-white/70">Refreshing...</span>
          </div>
        ) : hasImage ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-slate-400 border-t-white rounded-full animate-spin" />
              </div>
            )}
            <img
              src={referenceImage}
              alt={camera.name}
              className={clsx(
                'w-full h-full object-cover transition-opacity duration-300',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)} // Still mark as loaded to hide spinner
            />
            {/* Live indicator */}
            {liveSnapshot && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-green-600/90 backdrop-blur-sm text-white px-2 py-0.5 rounded text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                LIVE
              </div>
            )}
          </>
        ) : (
          // Placeholder icon when no image available
          <div className="relative z-10 w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center backdrop-blur-sm border border-slate-200">
            <VideoCameraIcon className="w-8 h-8 text-slate-400" />
          </div>
        )}

        {/* Status indicator and refresh button */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {/* Refresh button */}
          {camera.aiDevice?.status === 'online' && (
            <button
              onClick={handleRefresh}
              className={clsx(
                'p-1.5 rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-white transition-all',
                'opacity-0 group-hover:opacity-100',
                isRefreshing && 'animate-spin'
              )}
              title="Refresh thumbnail"
              disabled={isRefreshing}
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          )}
          {/* Status dot */}
          <span className="relative flex h-3 w-3">
            {config.pulse && (
              <span className={clsx(
                'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                config.dot
              )} />
            )}
            <span className={clsx(
              'relative inline-flex rounded-full h-3 w-3',
              config.dot,
              config.glow
            )} />
          </span>
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
          {effectiveStatus === 'online' ? (
            <button
              onClick={onView}
              className="btn btn-glass text-sm px-4 py-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Live
            </button>
          ) : (
            <div className="text-white text-sm text-center px-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              <p className="font-medium">Device Offline</p>
              <p className="text-xs text-white/70 mt-1">
                {camera.aiDevice ? 'AI engine not connected' : 'No device assigned'}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-slate-800 truncate flex-1">{camera.name}</h3>
          {!camera.isActive && (
            <span className="badge badge-neutral text-xs opacity-70">Inactive</span>
          )}
        </div>
        <p className="text-sm text-slate-500 truncate">{camera.location || 'No location set'}</p>

        <div className="flex items-center justify-between mt-4">
          <span className={clsx('badge text-xs', config.badge)}>
            {camera.status}
          </span>
          <div className="flex gap-1">
            {camera.isActive ? (
              <>
                <button
                  onClick={onView}
                  className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                  title="View Live"
                >
                  <EyeIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onEdit}
                  className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                  title="Edit Camera"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onConfigure}
                  className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-100 transition-all"
                  title="Configure Analytics"
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onSoftDelete}
                  className="p-2 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-100 transition-all"
                  title="Deactivate Camera"
                >
                  <StopIcon className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onRestore}
                  className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-100 transition-all"
                  title="Restore Camera"
                >
                  <CheckIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onHardDelete}
                  className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-100 transition-all"
                  title="Delete Camera"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CameraRow({ camera, index, onView, onConfigure, onEdit, onSoftDelete, onHardDelete, onRestore }) {
  const statusConfig = {
    online: 'badge-success',
    offline: 'bg-slate-100 text-slate-500 border border-slate-200',
    error: 'badge-critical',
  };

  const effectiveStatus = (camera.aiDevice && camera.aiDevice.status !== 'online')
    ? 'offline'
    : (camera.status || 'offline');

  return (
    <tr
      className={clsx("animate-fade-in", !camera.isActive && 'opacity-60')}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <td>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center border border-slate-200">
            <VideoCameraIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-800">{camera.name}</p>
              {!camera.isActive && (
                <span className="badge badge-neutral text-xs opacity-70">Inactive</span>
              )}
            </div>
            <p className="text-sm text-slate-400">{camera.code}</p>
          </div>
        </div>
      </td>
      <td className="text-slate-500">{camera.location || '-'}</td>
      <td>
        <span className={clsx('badge text-xs', statusConfig[effectiveStatus])}>
          {effectiveStatus}
        </span>
      </td>
      <td className="text-sm text-slate-500">
        {camera.lastSeen ? formatDateTime(camera.lastSeen) : 'Never'}
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={onView}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
            title="View Live"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          {camera.isActive ? (
            <>
              <button
                onClick={onEdit}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                title="Edit Camera"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onConfigure}
                className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-100 transition-all"
                title="Configure Analytics"
              >
                <Cog6ToothIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onSoftDelete}
                className="p-2 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-100 transition-all"
                title="Deactivate Camera"
              >
                <StopIcon className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onRestore}
                className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-100 transition-all"
                title="Restore Camera"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onHardDelete}
                className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-100 transition-all"
                title="Delete Camera"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function CameraDetailModal({ camera, onClose }) {
  const {
    connected,
    subscribeCamera,
    unsubscribeCamera,
    requestSnapshot,
    activeStreams,
    setAnnotated,
  } = useLiveStreamStore();

  const [streamIntent, setStreamIntent] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [quality, setQuality] = useState('medium');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [detectionMode, setDetectionMode] = useState(() => {
    try { return localStorage.getItem('cam_detection_mode') === 'true'; } catch { return false; }
  });
  const videoContainerRef = useRef(null);
  const streamState = activeStreams[camera.id];

  // If AI device is assigned but offline, camera is effectively offline
  const effectiveStatus = (camera.aiDevice && camera.aiDevice.status !== 'online')
    ? 'offline'
    : (camera.status || 'offline');
  // Derive the active/streaming UI from intent, but let a real stream error revert
  // it to the Start affordance instead of stranding on Stop with a dead frame.
  const isStreaming = streamIntent && streamState?.status !== 'error';

  const toggleFullscreen = useCallback(() => {
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    // Connect to live stream service on mount
    useLiveStreamStore.getState().connect();

    return () => {
      // Unsubscribe on unmount. Unconditional: the old `if (isStreaming)` guard
      // captured a stale value from this []-deps effect (always false at mount),
      // so an active stream was never torn down. unsubscribeCamera is a no-op when
      // nothing is subscribed.
      unsubscribeCamera(camera.id, camera.aiDevice?.id);
    };
  }, []);

  const handleStartStream = () => {
    subscribeCamera(camera.id, {
      quality,
      fps: quality === 'low' ? 5 : quality === 'high' ? 15 : 10,
      deviceId: camera.aiDevice?.id, // Pass AI Device ID for MQTT routing
      annotated: detectionMode,
    });
    setStreamIntent(true);
  };

  const handleStopStream = () => {
    unsubscribeCamera(camera.id, camera.aiDevice?.id);
    setStreamIntent(false);
  };

  const handleSnapshot = () => {
    requestSnapshot(camera.id, true, camera.aiDevice?.id);
    toast.success('Snapshot requested');
  };

  const handleToggleRecording = async () => {
    try {
      const { connectMqtt } = await import('../utils/mqttClient');
      const client = await connectMqtt();
      const action = isRecording ? 'stop' : 'start';
      const topic = `stream:request/${camera.aiDevice?.id}/record`;
      client.publish(topic, JSON.stringify({
        cameraId: camera.id,
        action,
        clientId: client.options.clientId,
        timestamp: new Date().toISOString(),
      }), { qos: 1 });
      setIsRecording(!isRecording);
      toast.success(isRecording ? 'Recording stopped' : 'Recording started');
    } catch {
      toast.error('Failed to toggle recording');
    }
  };

  const handleToggleDetection = () => {
    const next = !detectionMode;
    setDetectionMode(next);
    try { localStorage.setItem('cam_detection_mode', String(next)); } catch {}
    // Toggle annotated mode on active stream
    if (isStreaming) {
      setAnnotated(camera.id, next);
    }
  };

  const getStatusBadge = () => {
    if (!streamState) return null;
    const { status } = streamState;
    const colors = {
      connecting: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
      streaming: 'badge-success',
      device_offline: 'badge-critical',
      stopped: 'bg-slate-100 text-slate-500 border border-slate-200',
      offline: 'badge-critical',
    };
    return (
      <span className={clsx('badge text-xs', colors[status] || colors.stopped)}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className={clsx('modal-content', detectionMode ? 'max-w-6xl' : 'max-w-4xl')} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <VideoCameraIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="modal-title">{camera.name}</h3>
                <p className="text-sm text-slate-500">{camera.code}</p>
              </div>
              {isStreaming && getStatusBadge()}
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-body">
          {/* Live view + Detection panel layout */}
          <div className={clsx('flex flex-col lg:flex-row gap-4', detectionMode ? 'mb-0' : 'mb-5')}>
            {/* Video area */}
            <div className={clsx('flex-1 min-w-0', detectionMode && 'lg:flex-shrink-0')}>
              <div ref={videoContainerRef} className={clsx(
                "relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center overflow-hidden border border-slate-200",
                isFullscreen ? 'w-screen h-screen rounded-none border-0' : 'aspect-video'
              )}>
                {/* Grid pattern */}
                <div className="absolute inset-0 bg-grid-pattern opacity-30" />

                {streamState?.frame ? (
                  <img
                    src={streamState.frame}
                    alt="Live feed"
                    className="w-full h-full object-contain relative z-10"
                  />
                ) : streamState?.snapshot ? (
                  <img
                    src={streamState.snapshot}
                    alt="Snapshot"
                    className="w-full h-full object-contain relative z-10"
                  />
                ) : (
                  <div className="text-center text-slate-500 relative z-10">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-slate-50 flex items-center justify-center backdrop-blur-sm border border-slate-200">
                      <VideoCameraIcon className="w-10 h-10 text-slate-400" />
                    </div>
                    <p className="font-medium">Click Play to start live feed</p>
                    <p className="text-sm text-slate-400 mt-1">
                      {effectiveStatus === 'online'
                        ? 'Device online - Ready to stream'
                        : camera.aiDevice
                          ? 'Device offline - AI engine not connected'
                          : 'No device assigned to this camera'}
                    </p>
                  </div>
                )}

                {/* Fullscreen toggle */}
                <button
                  onClick={toggleFullscreen}
                  className="absolute top-3 right-3 z-20 bg-black/50 text-white p-2 rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm"
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="w-5 h-5" />
                  ) : (
                    <ArrowsPointingOutIcon className="w-5 h-5" />
                  )}
                </button>

                {/* Recording indicator */}
                {isRecording && (
                  <div className="absolute top-3 right-14 z-20 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" aria-hidden="true" />
                    REC
                  </div>
                )}

                {/* Stream overlay info */}
                {streamState?.status === 'streaming' && (
                  <div className="absolute top-3 left-3 flex items-center gap-2 z-20">
                    <span className="flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                      </span>
                      LIVE
                    </span>
                    {streamState.metadata && (
                      <span className="bg-black/50 text-white px-3 py-1.5 rounded-lg text-xs backdrop-blur-sm">
                        {streamState.metadata.width}x{streamState.metadata.height}
                      </span>
                    )}

                    {/* Detection mode indicator */}
                    {detectionMode && (
                      <span className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                        <CpuChipIcon className="w-3.5 h-3.5" />
                        AI
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Detection panel sidebar */}
            {detectionMode && (
              <div className="w-full lg:w-72 lg:flex-shrink-0 rounded-xl border border-slate-200 overflow-hidden">
                <DetectionPanel detections={streamState?.detections} />
              </div>
            )}
          </div>

          {/* Detection event feed */}
          {detectionMode && (
            <div className="mb-5 mt-3 rounded-xl border border-slate-200 overflow-hidden h-48">
              <DetectionEventFeed detections={streamState?.detections} />
            </div>
          )}

          {!detectionMode && <div className="mb-5" />}

          {/* Stream controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5 p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              {!isStreaming ? (
                <button
                  onClick={handleStartStream}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={effectiveStatus !== 'online'}
                  title={effectiveStatus === 'online'
                    ? 'Start live video stream'
                    : 'Camera device is offline'}
                >
                  <PlayIcon className="w-5 h-5" />
                  Start Live Feed
                </button>
              ) : (
                <button
                  onClick={handleStopStream}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <StopIcon className="w-5 h-5" />
                  Stop
                </button>
              )}

              <button
                onClick={handleSnapshot}
                className="btn btn-secondary flex items-center gap-2"
                disabled={effectiveStatus !== 'online'}
                title={effectiveStatus === 'online'
                  ? 'Capture snapshot'
                  : 'Camera device is offline'}
              >
                <CameraIcon className="w-5 h-5" />
                Snapshot
              </button>

              <button
                onClick={handleToggleRecording}
                className={clsx(
                  'btn flex items-center gap-2',
                  isRecording ? 'bg-red-600 text-white hover:bg-red-700' : 'btn-secondary'
                )}
                disabled={!isStreaming}
                title={isRecording ? 'Stop recording' : 'Start recording'}
                aria-label={isRecording ? 'Stop recording' : 'Start recording'}
              >
                <span className={clsx('w-3 h-3 rounded-full', isRecording ? 'bg-white animate-pulse' : 'bg-red-500')} aria-hidden="true" />
                {isRecording ? 'Stop Rec' : 'Record'}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleToggleDetection}
                className={clsx(
                  'btn flex items-center gap-2 text-sm',
                  detectionMode
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'btn-secondary'
                )}
                disabled={!isStreaming}
                title={detectionMode ? 'Turn off AI detection overlay' : 'Show AI detection bounding boxes and data'}
              >
                <CpuChipIcon className="w-4 h-4" />
                AI Detection
              </button>

              <label className="text-sm text-slate-500">Quality:</label>
              <select
                value={quality}
                onChange={(e) => setQuality(e.target.value)}
                className="input py-2 px-3 text-sm min-w-[120px]"
                disabled={isStreaming}
              >
                <option value="low">Low (5 FPS)</option>
                <option value="medium">Medium (10 FPS)</option>
                <option value="high">High (15 FPS)</option>
              </select>
            </div>
          </div>

          {/* Camera details grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Status</p>
              <span className={clsx(
                'badge text-xs',
                effectiveStatus === 'online' ? 'badge-success' : effectiveStatus === 'error' ? 'badge-critical' : 'bg-slate-100 text-slate-500 border border-slate-200'
              )}>
                {effectiveStatus}
              </span>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Location</p>
              <p className="font-medium text-slate-800">{camera.location || '-'}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">RTSP URL</p>
              <p className="font-medium text-slate-800 text-sm truncate">{camera.rtspUrl || '-'}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">AI Device</p>
              <div className="flex items-center gap-2">
                <p className="font-medium text-slate-800">
                  {camera.aiDevice?.name || 'Not assigned'}
                </p>
                {camera.aiDevice && (
                  <span className={clsx(
                    'badge text-xs',
                    camera.aiDevice.status === 'online' ? 'badge-success' : 'bg-slate-100 text-slate-500 border border-slate-200'
                  )}>
                    {camera.aiDevice.status}
                  </span>
                )}
              </div>
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-secondary">Close</button>
            <button className="btn btn-primary">Edit Camera</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddCameraModal({ siteId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    rtspUrl: '',
    rtspUsername: '',
    rtspPassword: '',
    location: '',
    direction: '',
    coverage: '',
    aiDeviceId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: devicesData } = useQuery({
    queryKey: ['devices', siteId],
    queryFn: () => devicesAPI.getDevices({ siteId }),
    enabled: !!siteId,
  });
  const devices = devicesData?.data?.data || devicesData?.data || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.rtspUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!siteId) {
      toast.error('Please select a site first');
      return;
    }

    setIsSubmitting(true);

    try {
      await camerasAPI.createCamera({
        ...formData,
        siteId,
        aiDeviceId: formData.aiDeviceId || null,
      });
      toast.success('Camera added successfully');
      onSuccess();
    } catch (error) {
      console.error('Failed to add camera:', error);
      toast.error(error.response?.data?.error || 'Failed to add camera');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <PlusIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="modal-title">Add New Camera</h3>
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Camera Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Main Entrance Camera"
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Camera Code <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., CAM-001"
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              RTSP URL <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="rtspUrl"
              value={formData.rtspUrl}
              onChange={handleChange}
              placeholder="rtsp://192.168.1.100:554/stream"
              className="input"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                RTSP Username
              </label>
              <input
                type="text"
                name="rtspUsername"
                value={formData.rtspUsername}
                onChange={handleChange}
                placeholder="admin"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                RTSP Password
              </label>
              <input
                type="password"
                name="rtspPassword"
                value={formData.rtspPassword}
                onChange={handleChange}
                placeholder="password"
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Building A - Ground Floor"
              className="input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Direction
              </label>
              <select
                name="direction"
                value={formData.direction}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select direction</option>
                <option value="north">North</option>
                <option value="south">South</option>
                <option value="east">East</option>
                <option value="west">West</option>
                <option value="northeast">Northeast</option>
                <option value="northwest">Northwest</option>
                <option value="southeast">Southeast</option>
                <option value="southwest">Southwest</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Coverage Area
              </label>
              <input
                type="text"
                name="coverage"
                value={formData.coverage}
                onChange={handleChange}
                placeholder="e.g., Entry zone"
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              AI Device
            </label>
            <select
              name="aiDeviceId"
              value={formData.aiDeviceId}
              onChange={handleChange}
              className="input"
            >
              <option value="">Select AI device</option>
              {devices.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.status === 'online' ? 'Online' : 'Offline'})
                </option>
              ))}
            </select>
          </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-5 h-5" />
                    Add Camera
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function MultiCameraGrid({ cameras, layout, onViewCamera }) {
  const { subscribeCamera, unsubscribeCamera, activeStreams, connected } = useLiveStreamStore();
  const cols = layout === '3x3' ? 3 : 2;
  const totalSlots = cols * cols;
  const displayCameras = cameras.slice(0, totalSlots);

  useEffect(() => {
    if (!connected) return;
    displayCameras.forEach((cam) => {
      if (!activeStreams[cam.id]?.subscribed) {
        subscribeCamera(cam.id, { quality: 'low' });
      }
    });
    return () => {
      displayCameras.forEach((cam) => {
        if (activeStreams[cam.id]?.subscribed) {
          unsubscribeCamera(cam.id, cam.aiDeviceId);
        }
      });
    };
  }, [connected, displayCameras.map(c => c.id).join(',')]);

  return (
    <div
      className="animate-fade-in"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '0.75rem',
      }}
    >
      {Array.from({ length: totalSlots }).map((_, idx) => {
        const cam = displayCameras[idx];
        if (!cam) {
          return (
            <div key={`empty-${idx}`} className="glass-card aspect-video flex items-center justify-center">
              <VideoCameraIcon className="w-10 h-10 text-slate-300" />
            </div>
          );
        }
        const stream = activeStreams[cam.id];
        return (
          <div
            key={cam.id}
            className="glass-card p-0 overflow-hidden cursor-pointer group relative"
            onClick={() => onViewCamera(cam)}
          >
            <div className="aspect-video bg-slate-900 relative">
              {stream?.frame ? (
                <img
                  src={stream.frame}
                  alt={cam.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-600 border-t-slate-300" />
                  <span className="text-xs">Connecting...</span>
                </div>
              )}
              {/* Camera name overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium truncate">{cam.name}</span>
                  {stream?.subscribed && (
                    <span className="flex items-center gap-1 text-xs text-red-400 font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      LIVE
                    </span>
                  )}
                </div>
              </div>
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View Full
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EditCameraModal({ camera, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: camera?.name || '',
    code: camera?.code || '',
    rtspUrl: camera?.rtspUrl || '',
    rtspUsername: camera?.rtspUsername || '',
    rtspPassword: camera?.rtspPassword || '',
    location: camera?.location || '',
    direction: camera?.direction || '',
    coverage: camera?.coverage || '',
    mountHeight: camera?.mountHeight || null,
    fieldOfView: camera?.fieldOfView || null,
    aiDeviceId: camera?.aiDeviceId || null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Camera name is required';
    if (!formData.code.trim()) newErrors.code = 'Camera code is required';
    if (!formData.rtspUrl.trim()) newErrors.rtspUrl = 'RTSP URL is required';
    if (formData.mountHeight && isNaN(parseFloat(formData.mountHeight))) {
      newErrors.mountHeight = 'Must be a valid number';
    }
    if (formData.fieldOfView && isNaN(parseFloat(formData.fieldOfView))) {
      newErrors.fieldOfView = 'Must be a valid number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Prepare data: convert empty strings to null for numeric fields
      const submitData = {
        ...formData,
        mountHeight: formData.mountHeight === '' ? null : formData.mountHeight,
        fieldOfView: formData.fieldOfView === '' ? null : formData.fieldOfView,
        aiDeviceId: formData.aiDeviceId === '' ? null : formData.aiDeviceId,
        rtspUsername: formData.rtspUsername === '' ? null : formData.rtspUsername,
        rtspPassword: formData.rtspPassword === '' ? null : formData.rtspPassword,
        location: formData.location === '' ? null : formData.location,
        direction: formData.direction === '' ? null : formData.direction,
        coverage: formData.coverage === '' ? null : formData.coverage,
      };

      await camerasAPI.updateCamera(camera.id, submitData);
      toast.success('Camera updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to update camera';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <CheckIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="modal-title">Edit Camera</h3>
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Camera Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Entrance Front Gate"
                  className={`input w-full ${errors.name ? 'input-error' : ''}`}
                />
                {errors.name && <span className="text-xs text-red-500 mt-1">{errors.name}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Camera Code <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="e.g., CAM-001"
                  className={`input w-full ${errors.code ? 'input-error' : ''}`}
                />
                {errors.code && <span className="text-xs text-red-500 mt-1">{errors.code}</span>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  RTSP URL <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="rtspUrl"
                  value={formData.rtspUrl}
                  onChange={handleChange}
                  placeholder="rtsp://192.168.1.100:554/stream"
                  className={`input w-full ${errors.rtspUrl ? 'input-error' : ''}`}
                />
                {errors.rtspUrl && <span className="text-xs text-red-500 mt-1">{errors.rtspUrl}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  RTSP Username
                </label>
                <input
                  type="text"
                  name="rtspUsername"
                  value={formData.rtspUsername}
                  onChange={handleChange}
                  placeholder="admin"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  RTSP Password
                </label>
                <input
                  type="password"
                  name="rtspPassword"
                  value={formData.rtspPassword}
                  onChange={handleChange}
                  placeholder="password"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Main Entrance"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Direction
                </label>
                <input
                  type="text"
                  name="direction"
                  value={formData.direction}
                  onChange={handleChange}
                  placeholder="e.g., North"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Coverage Area
                </label>
                <input
                  type="text"
                  name="coverage"
                  value={formData.coverage}
                  onChange={handleChange}
                  placeholder="e.g., Lobby"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Mount Height (m)
                </label>
                <input
                  type="number"
                  name="mountHeight"
                  value={formData.mountHeight}
                  onChange={handleChange}
                  placeholder="e.g., 2.5"
                  step="0.1"
                  className={`input w-full ${errors.mountHeight ? 'input-error' : ''}`}
                />
                {errors.mountHeight && (
                  <span className="text-xs text-red-500 mt-1">{errors.mountHeight}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Field of View (°)
                </label>
                <input
                  type="number"
                  name="fieldOfView"
                  value={formData.fieldOfView}
                  onChange={handleChange}
                  placeholder="e.g., 90"
                  step="0.1"
                  className={`input w-full ${errors.fieldOfView ? 'input-error' : ''}`}
                />
                {errors.fieldOfView && (
                  <span className="text-xs text-red-500 mt-1">{errors.fieldOfView}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  AI Device ID
                </label>
                <input
                  type="text"
                  name="aiDeviceId"
                  value={formData.aiDeviceId}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="input w-full"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── DeviceConfigModal ────────────────────────────────────────────────────────
const DEFAULT_HW = {
  maxWorkers: 6, minWorkers: 2, maxCameras: 12,
  batchSize: 1, gpuDevice: 0, useGpu: true, useFp16: true,
  maxFps: 30, frameSkip: 2,
  personModelPath: 'models/yolo11s.pt',
  ppeModelPath: 'models/ppe_hexmon_v8m.pt',
  poseModelPath: 'models/yolo11m-pose.pt',
  fireModelPath: 'models/fire_yolo11n.pt',
};

function DeviceConfigModal({ cameras, onClose }) {
  const queryClient = useQueryClient();
  // Get aiDevice from the first camera that has one — already loaded on the page
  const device = cameras?.find(c => c.aiDevice)?.aiDevice || null;
  const [hw, setHw] = useState(DEFAULT_HW);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (device?.config?.hardware) setHw({ ...DEFAULT_HW, ...device.config.hardware });
  }, [device?.id]);

  const updateHw = (key, value) => setHw(p => ({ ...p, [key]: value }));

  const handleSave = async () => {
    if (!device) return;
    setIsSaving(true);
    try {
      await devicesAPI.updateAIDevice(device.id, { config: { ...(device.config || {}), hardware: hw } });
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      toast.success('AI device config saved — engine applies on next sync (~30s)');
      onClose();
    } catch { toast.error('Failed to save device config'); }
    finally { setIsSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CpuChipIcon className="w-5 h-5" /> AI Device Config
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {device ? `${device.name} (${device.type || 'gpu_server'})` : 'No device for this site'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-6 h-6" /></button>
        </div>

        {!device ? (
          <div className="p-6 text-center text-gray-500">No AI device provisioned for this site.</div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-6 flex-1">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b pb-1">Worker Scaling</h3>
              <div className="grid grid-cols-3 gap-3">
                {[{key:'minWorkers',label:'Min Workers',min:1,max:8},{key:'maxWorkers',label:'Max Workers',min:1,max:16},{key:'maxCameras',label:'Max Cameras',min:1,max:32}].map(({key,label,min,max})=>(
                  <div key={key}>
                    <label className="text-xs text-gray-600 block mb-1">{label}</label>
                    <input type="number" min={min} max={max} value={hw[key]}
                      onChange={e=>updateHw(key,parseInt(e.target.value))} className="input w-full text-sm" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">Rule of thumb: maxWorkers = ceil(cameras / 2)</p>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b pb-1">GPU / Inference</h3>
              <div className="grid grid-cols-3 gap-3">
                {[{key:'gpuDevice',label:'GPU Device ID',min:0,max:7},{key:'batchSize',label:'Batch Size',min:1,max:16},{key:'maxFps',label:'Max FPS',min:1,max:60}].map(({key,label,min,max})=>(
                  <div key={key}>
                    <label className="text-xs text-gray-600 block mb-1">{label}</label>
                    <input type="number" min={min} max={max} value={hw[key]}
                      onChange={e=>updateHw(key,parseInt(e.target.value))} className="input w-full text-sm" />
                  </div>
                ))}
              </div>
              <div className="flex gap-6">
                {[{key:'useGpu',label:'Use GPU (CUDA)'},{key:'useFp16',label:'Use FP16 (half precision)'}].map(({key,label})=>(
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!hw[key]} onChange={e=>updateHw(key,e.target.checked)} className="w-4 h-4 rounded accent-blue-600" />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Frame Skip (0 = process every frame)</label>
                <input type="number" min={0} max={10} value={hw.frameSkip}
                  onChange={e=>updateHw('frameSkip',parseInt(e.target.value))} className="input w-32 text-sm" />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b pb-1">Model Paths</h3>
              {[{key:'personModelPath',label:'Person / General'},{key:'ppeModelPath',label:'PPE Detection'},{key:'poseModelPath',label:'Pose / Fall'},{key:'fireModelPath',label:'Fire / Smoke'}].map(({key,label})=>(
                <div key={key}>
                  <label className="text-xs text-gray-600 block mb-1">{label}</label>
                  <input type="text" value={hw[key]} onChange={e=>updateHw(key,e.target.value)}
                    placeholder={DEFAULT_HW[key]} className="input w-full text-sm font-mono" />
                </div>
              ))}
              <p className="text-xs text-gray-500">Relative to ai-engine working directory. Leave default unless using a custom model.</p>
            </div>
          </div>
        )}

        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          {device && (
            <button onClick={handleSave} disabled={isSaving} className="btn btn-primary flex items-center gap-2">
              {isSaving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" /> : <CheckIcon className="w-4 h-4" />}
              Save & Apply
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
